import { Link } from '@tanstack/react-router';
import { ArrowUpDown, Calendar, Menu, Plus, Search } from 'lucide-react';
import { useMemo, useState } from 'react';

import { useGetAllRecitals } from '~entities/recital-manager/api/get-all-recitals';
import { useCurrentUserRole } from '~entities/users/hooks/use-current-user-role';

import { Badge } from '~/shared/shadcn/ui/badge';
import { Button } from '~/shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '~/shared/shadcn/ui/card';
import { Input } from '~/shared/shadcn/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/shared/shadcn/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '~/shared/shadcn/ui/sheet';

import { RecitalCard } from './recital-card';

export const RecitalListTable = () => {
  const { hasAdminAccess } = useCurrentUserRole();
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12, // Cards work better with 12 per page
  });
  const [filters, setFilters] = useState({
    query: '',
    status: 'all' as 'all' | 'draft' | 'published' | 'archived',
    year: 'all',
  });
  const [sortBy, setSortBy] = useState<'name' | 'year' | 'date'>('date');
  const [recitalActionsOpen, setRecitalActionsOpen] = useState(false);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.status !== 'all') count++;
    if (filters.year !== 'all') count++;
    if (sortBy !== 'date') count++;
    return count;
  }, [filters.status, filters.year, sortBy]);

  // Fetch recitals
  const { data, isLoading } = useGetAllRecitals({
    page: pagination.page,
    limit: pagination.limit,
    ...(filters.status !== 'all' && { status: filters.status }),
    ...(filters.year !== 'all' && { year: parseInt(filters.year) }),
  });

  // Get unique years for filter
  const availableYears = useMemo(() => {
    if (!data?.data) return [];
    const years = [...new Set(data.data.map((r) => r.year))];
    return years.sort((a, b) => b - a);
  }, [data]);

  // Filter and sort recitals
  const filteredRecitals = useMemo(() => {
    let recitals = data?.data ?? [];

    // Filter by search query
    if (filters.query) {
      const query = filters.query.toLowerCase();
      recitals = recitals.filter((r) => r.name.toLowerCase().includes(query));
    }

    // Sort
    recitals = [...recitals].sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'year':
          return b.year - a.year;
        case 'date':
        default:
          return (
            new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
          );
      }
    });

    return recitals;
  }, [data?.data, filters.query, sortBy]);

  const renderRecitalFilters = (stacked: boolean) => {
    const triggerClassName = stacked ? 'w-full' : 'w-[180px]';

    return (
      <>
        <div className={stacked ? 'space-y-2' : undefined}>
          {stacked ? (
            <label className='block text-sm font-medium'>Sort</label>
          ) : null}
          <Select
            value={sortBy}
            onValueChange={(value: 'year' | 'name' | 'date') => {
              setSortBy(value);
            }}
          >
            <SelectTrigger className={triggerClassName}>
              {!stacked ? <ArrowUpDown className='mr-2 size-4' /> : null}
              <SelectValue placeholder='Sort by' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='date'>Sort by Date</SelectItem>
              <SelectItem value='name'>Sort by Name</SelectItem>
              <SelectItem value='year'>Sort by Year</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className={stacked ? 'space-y-2' : undefined}>
          {stacked ? (
            <label className='block text-sm font-medium'>Status</label>
          ) : null}
          <Select
            value={filters.status}
            onValueChange={(
              value: 'all' | 'draft' | 'published' | 'archived',
            ) => {
              setFilters({ ...filters, status: value });
            }}
          >
            <SelectTrigger className={triggerClassName}>
              <SelectValue placeholder='Status' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All Status</SelectItem>
              <SelectItem value='draft'>Draft</SelectItem>
              <SelectItem value='published'>Published</SelectItem>
              <SelectItem value='archived'>Archived</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className={stacked ? 'space-y-2' : undefined}>
          {stacked ? (
            <label className='block text-sm font-medium'>Year</label>
          ) : null}
          <Select
            value={filters.year}
            onValueChange={(value) => {
              setFilters({ ...filters, year: value });
            }}
          >
            <SelectTrigger className={triggerClassName}>
              <SelectValue placeholder='Year' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All Years</SelectItem>
              {availableYears.map((year) => (
                <SelectItem key={year} value={year.toString()}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </>
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className='flex items-center justify-between'>
          <div>
            <CardTitle className='text-3xl'>All Recitals</CardTitle>
            <p className='mt-1 text-sm text-muted-foreground'>
              Manage your recitals, shows, and performances
            </p>
          </div>
          {hasAdminAccess && (
            <Button asChild className='hidden shrink-0 md:inline-flex'>
              <Link to='/recitals/new'>
                <Plus />
                Create Recital
              </Link>
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* Search + filters */}
        <div className='mb-6 flex flex-col gap-4 md:flex-row md:flex-wrap'>
          <div className='min-w-0 flex-1 md:min-w-[200px]'>
            <div className='relative'>
              <Search className='absolute left-3 top-2.5 size-4 text-muted-foreground' />
              <Input
                placeholder='Search recitals...'
                value={filters.query}
                onChange={(e) => {
                  setFilters({ ...filters, query: e.target.value });
                }}
                className='pl-9'
              />
            </div>
          </div>

          <Sheet open={recitalActionsOpen} onOpenChange={setRecitalActionsOpen}>
            <SheetTrigger asChild>
              <Button variant='outline' className='relative w-full md:hidden'>
                <Menu className='mr-2 size-4' />
                Recital actions
                {activeFilterCount > 0 ? (
                  <Badge className='ml-2' variant='secondary'>
                    {activeFilterCount}
                  </Badge>
                ) : null}
              </Button>
            </SheetTrigger>
            <SheetContent side='right' className='w-[300px] overflow-y-auto'>
              <SheetHeader>
                <SheetTitle>Recital actions</SheetTitle>
                <SheetDescription>
                  Create a recital or sort and filter this list.
                </SheetDescription>
              </SheetHeader>
              <div className='mt-6 flex flex-col gap-4'>
                {hasAdminAccess ? (
                  <Button className='w-full justify-start' asChild>
                    <Link
                      to='/recitals/new'
                      onClick={() => {
                        setRecitalActionsOpen(false);
                      }}
                    >
                      <Plus className='mr-2 size-4' />
                      Create Recital
                    </Link>
                  </Button>
                ) : null}
                {renderRecitalFilters(true)}
              </div>
            </SheetContent>
          </Sheet>

          <div className='hidden flex-wrap gap-4 md:flex'>
            {renderRecitalFilters(false)}
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className='py-12 text-center'>
            <div className='text-muted-foreground'>Loading recitals...</div>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && filteredRecitals.length === 0 && (
          <div className='rounded-lg border-2 border-dashed py-12 text-center'>
            <Calendar className='mx-auto mb-4 size-12 text-muted-foreground' />
            <h3 className='mb-2 text-lg font-semibold'>No recitals found</h3>
            <p className='mb-4 text-muted-foreground'>
              {filters.query ||
              filters.status !== 'all' ||
              filters.year !== 'all'
                ? 'Try adjusting your filters'
                : 'Get started by creating your first recital'}
            </p>
            {!filters.query &&
              filters.status === 'all' &&
              filters.year === 'all' && (
                <Button asChild>
                  <Link to='/recitals/new'>
                    <Plus />
                    Create Your First Recital
                  </Link>
                </Button>
              )}
          </div>
        )}

        {/* Cards Grid */}
        {!isLoading && filteredRecitals.length > 0 && (
          <>
            <div className='grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
              {filteredRecitals.map((recital) => (
                <RecitalCard key={recital._id} recital={recital} />
              ))}
            </div>

            {/* Pagination Info */}
            <div className='mt-6 flex items-center justify-between text-sm text-muted-foreground'>
              <div>
                Showing {filteredRecitals.length} of{' '}
                {data?.pagination.totalCount ?? 0} recitals
              </div>
              {data?.pagination.totalPages &&
                data.pagination.totalPages > 1 && (
                  <div className='flex gap-2'>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => {
                        setPagination({
                          ...pagination,
                          page: pagination.page - 1,
                        });
                      }}
                      disabled={pagination.page === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => {
                        setPagination({
                          ...pagination,
                          page: pagination.page + 1,
                        });
                      }}
                      disabled={
                        pagination.page >= (data.pagination.totalPages || 1)
                      }
                    >
                      Next
                    </Button>
                  </div>
                )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
